import { Link, useParams } from "@tanstack/react-router";
import { useDeferredValue, useState } from "react";
import { useRole } from "../../components/AppShell";
import { BackIcon, SearchIcon } from "../../components/icons";
import { Avatar, Empty, ListSkeleton, StatusPill } from "../../components/ui";
import { CONTACT_TYPE_LABEL, formatPhone, shortTime } from "../../lib/format";
import { useContact, useContacts } from "../../lib/queries";
import { ContactCases } from "../cases/ContactCases";
import { ContactForm } from "./ContactForm";
import { ContactImportDialog } from "./ContactImport";

export function ContactsPage() {
  const [q, setQ] = useState("");
  const deferred = useDeferredValue(q.trim());
  const contacts = useContacts(deferred);
  const count = contacts.data?.items.length;
  const role = useRole();
  const [importing, setImporting] = useState(false);

  return (
    <div className="page">
      <div className="page-head">
        <h1>Contactos{count ? <span className="page-count">{count}</span> : null}</h1>
        <label className="search-field">
          <SearchIcon />
          <input
            className="input"
            type="search"
            placeholder="Buscar por nombre o teléfono"
            aria-label="Buscar contactos"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </label>
        {(role === "owner" || role === "admin") && (
          <button className="btn" type="button" onClick={() => setImporting(true)}>
            Importar
          </button>
        )}
      </div>
      {importing && <ContactImportDialog onClose={() => setImporting(false)} />}
      <div className="card">
        {contacts.isPending ? (
          <ListSkeleton />
        ) : contacts.data?.items.length === 0 ? (
          <Empty title={deferred ? "Ningún contacto coincide." : "Todavía no hay contactos."}>
            <span>Cada persona que escribe al WhatsApp de la empresa se agrega aquí sola.</span>
          </Empty>
        ) : (
          <ul className="entity-list">
            {contacts.data?.items.map((c) => (
              <li key={c.id}>
                <Link to="/contactos/$contactId" params={{ contactId: c.id }} className="entity-row">
                  <Avatar name={c.name} fallback={c.waId} />
                  <span className="entity-main">
                    <span className="entity-name">{c.name ?? "Sin nombre"}</span>
                    <span className="entity-meta">
                      <span className="mono">{formatPhone(c.waId)}</span>
                      {c.province && <span>{c.province}</span>}
                    </span>
                  </span>
                  <span className="entity-flags">
                    {c.type && <span className="pill pill-closed">{CONTACT_TYPE_LABEL[c.type]}</span>}
                    {c.tags.slice(0, 2).map((t) => (
                      <span key={t} className="tag">
                        {t}
                      </span>
                    ))}
                    <span className="faint entity-time">{shortTime(c.updatedAt)}</span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export function ContactPage() {
  const { contactId } = useParams({ from: "/app/contactos/$contactId" });
  const contact = useContact(contactId);

  if (contact.isPending) return <div className="page">Cargando…</div>;
  if (contact.isError) return <div className="page">{contact.error.message}</div>;
  const c = contact.data;

  return (
    <div className="page">
      <Link className="back-link" to="/contactos">
        <BackIcon />
        Contactos
      </Link>
      <div className="card contact-hero">
        <Avatar name={c.name} fallback={c.waId} />
        <div className="contact-hero-text">
          <h1>{c.name ?? "Sin nombre"}</h1>
          <span className="muted mono">{formatPhone(c.waId)}</span>
          <span className="contact-hero-tags">
            {c.type && <span className="pill pill-closed">{CONTACT_TYPE_LABEL[c.type]}</span>}
            {c.organization && <span className="pill pill-human">{c.organization.name}</span>}
            {c.province && <span className="tag">{c.province}</span>}
            {c.tags.map((t) => (
              <span key={t} className="tag">
                {t}
              </span>
            ))}
          </span>
        </div>
      </div>
      <div className="contact-layout">
        <div className="card contact-conversations">
          <div className="card-body">
            <h3>Conversaciones</h3>
            {c.conversations.length === 0 && <p className="muted">Todavía no ha escrito.</p>}
            {c.conversations.map((conv) => (
              <Link
                key={conv.id}
                className="conv-link"
                to="/bandeja"
                search={{ c: conv.id, f: conv.status === "closed" ? "closed" : "all" }}
              >
                <StatusPill status={conv.status} />
                <span className="conv-link-preview">{conv.lastMessagePreview}</span>
                <span className="faint">{shortTime(conv.lastMessageAt)}</span>
              </Link>
            ))}
            <h3 className="contact-cases-title">Postventa y ventas</h3>
            <ContactCases contact={c} />
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <h3>Ficha</h3>
            <ContactForm key={c.updatedAt} contact={c} />
          </div>
        </div>
      </div>
    </div>
  );
}
